/**
 * Shared scroll-reveal group (FLYR-637).
 *
 * Put `x-data="reveal"` on a wrapper and `x-intersect.once="show($el)"` on each
 * child that should animate in, then give the child one of the `.reveal-*`
 * primitives from src/tailwind/_components.css.
 *
 * Children cascade in one at a time. Arrivals are collected for a frame and then
 * sorted into document order before their delays are handed out, because Alpine's
 * intersect plugin observes each element separately and delivers callbacks for a
 * batch in whatever order it likes, which otherwise runs the cascade backwards.
 * Each frame starts a fresh cascade at 0ms, so a group that scrolls in piecemeal
 * never inherits an ever-growing delay.
 *
 * The hidden-pending-reveal state hangs off the `js-reveal-ready` class this
 * component adds, so children stay visible when JS never runs or when the
 * visitor asks for reduced motion. Blitz serves cached HTML, so that gate has to
 * be applied client-side rather than baked into the markup.
 */
const STAGGER_MS = 120;
const MAX_STEPS = 7;

/** Document order, so the cascade follows the markup rather than callback order. */
function byDocumentPosition(a, b) {
    return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
}

export default () => {
    // Kept out of the Alpine data object on purpose: these hold DOM nodes, which
    // have no business being wrapped in a reactive proxy.
    let pending = [];
    let scheduled = false;

    return {
        init() {
            if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                this.$el.classList.add('js-reveal-ready');
            }
        },

        show(el) {
            pending.push(el);

            if (scheduled) {
                return;
            }

            scheduled = true;

            requestAnimationFrame(() => {
                const batch = pending.sort(byDocumentPosition);
                pending = [];
                scheduled = false;

                batch.forEach((item, i) => {
                    item.style.transitionDelay = `${Math.min(i, MAX_STEPS) * STAGGER_MS}ms`;
                    item.classList.add('is-visible');
                });
            });
        },
    };
};
